<template>
    <nav class="rail" :class="{ 'rail--busy': !!loadingKey }" data-cy="layout-sidebar-rail">
        <template v-for="group in groups" :key="group.label">
            <span v-if="group.atBottom && group.first" class="rail__grow"></span>

            <span class="rail__group">{{ group.label }}</span>

            <a-tooltip
                v-for="item in group.items"
                :key="item.key"
                placement="right"
                color="#1F2630"
                :mouse-enter-delay="0.15"
                overlay-class-name="nav-tip"
            >
                <template #title>
                    <span v-if="item.locked" class="nav-tip__badge">
                        <lock-outlined />
                        MCP ACCESS ONLY
                    </span>

                    <b class="nav-tip__title">{{ item.text }}</b>

                    <span class="nav-tip__desc">{{ item.description }}</span>

                    <span v-if="item.actions" class="nav-tip__actions">
                        <span v-for="action in item.actions" :key="action">{{ action }}</span>
                    </span>

                    <span v-if="item.footnote" class="nav-tip__desc">{{ item.footnote }}</span>
                </template>

                <component
                    :is="wrapper(item).is"
                    v-bind="wrapper(item).attrs"
                    class="navitem"
                    :class="{
                        'navitem--on': isSelected(item),
                        'navitem--locked': item.locked,
                        'navitem--loading': isLoading(item)
                    }"
                    :aria-busy="isLoading(item) ? 'true' : null"
                    :data-cy="`layout-sidebar-item-${item.key}`"
                    @click="handleMenuClick(item, $event)"
                >
                    <span v-if="item.isNew" class="navitem__new"></span>

                    <loading-outlined v-if="isLoading(item)" spin class="navitem__icon" />

                    <component :is="item.icon" v-else class="navitem__icon" />

                    <span class="navitem__name">
                        <span v-for="word in item.text.split(' ')" :key="word">{{ word }}</span>
                    </span>

                    <lock-outlined v-if="item.locked" class="navitem__lock" />
                </component>
            </a-tooltip>
        </template>
    </nav>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { url } from '@/config';
import { LockOutlined, ApiOutlined, LoadingOutlined } from '@ant-design/icons-vue';
import DashboardIcon from '@/assets/images/components/nav/dashboard-icon';
import LiveSearchIcon from '@/assets/images/components/nav/live-search-icon';
import HelperIcon from '@/assets/images/components/nav/helper-icon';
import EngageIcon from '@/assets/images/components/nav/engage-icon';
import HowToIcon from '@/assets/images/components/nav/how-to-icon';
import CustomTopicsIcon from '@/assets/images/components/nav/custom-topics-icon';
import AutoModerationIcon from '@/assets/images/components/nav/auto-moderation-icon';
// import DataGovernanceIcon from '@/assets/images/components/nav/data-governance-icon';
import { ACTION_TYPE_PAGE_VIEW, EVENT_TYPE_CLICK, ACTION_MODULE_NEW_UI_NAVIGATION_CHANGE } from '@/assets/common/account-events';

const PAINT_TIMEOUT = 150;

const MODERATION_ACTIONS = ['Hide', 'Label', 'Hide & Block', 'Review', 'Assign', '+ any action you need'];

export default {
    components: {
        LockOutlined,
        LoadingOutlined
    },

    props: {
        currentKey: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            loadingKey: null
        };
    },

    computed: {
        ...mapGetters({
            isAdmin: 'auth/isAdmin',
            isSysAdmin: 'auth/isSysAdmin',
            isMcpUser: 'auth/isMcpUser'
        }),

        canModerate() {
            return this.isAdmin || this.isSysAdmin || this.isMcpUser;
        },

        groups() {
            const locked = this.isMcpUser;

            const analyze = [
                {
                    key: 'dashboard',
                    text: 'Dashboard',
                    description: 'See the big picture. Sentiment, trend, sources and impact for any brand and timeframe.',
                    icon: DashboardIcon,
                    href: this.buildUrl('/n/'), // href because to fix removing meta viewport on helper
                    value: '/',
                    tracked: true,
                    locked
                },
                {
                    key: 'live-search',
                    text: 'Live Search',
                    description: 'Search every source. Define the queries that feed into everything else.',
                    icon: LiveSearchIcon,
                    href: this.buildUrl('/search'),
                    tracked: true,
                    locked
                },
                {
                    key: 'helper',
                    text: 'Helper',
                    description: 'Ask questions in plain language on any brand, topic and timeframe. Pin the ones you reuse.',
                    icon: HelperIcon,
                    href: this.buildUrl('/n/helper'),
                    value: '/helper',
                    tracked: true,
                    locked
                },
                {
                    key: 'engage',
                    text: 'Engage',
                    description: 'Reply, review, label and assign from every connected account in one feed.',
                    icon: EngageIcon,
                    href: this.buildUrl('/inbox'),
                    tracked: true,
                    locked
                }
            ];

            const support = [
                {
                    key: 'how-to',
                    text: 'How To',
                    description: 'Guides and short videos on every part of Affogata.',
                    icon: HowToIcon,
                    href: this.buildUrl('/n/how_to'),
                    value: '/how_to'
                }
            ];

            if (this.isMcpUser) {
                support.push({
                    key: 'mcp-access',
                    text: 'MCP Access',
                    description: 'Your MCP endpoint, keys and setup guides.',
                    icon: ApiOutlined,
                    href: this.buildUrl('/n/mcp_access'),
                    value: '/mcp_access'
                });
            }

            const manage = [
                {
                    key: 'custom-topics',
                    text: 'Custom Topics',
                    description: 'Group mentions by phrase. Topics feed dashboards, Helper and alerts.',
                    icon: CustomTopicsIcon,
                    href: this.buildUrl('/n/topics'),
                    value: '/topics',
                    tracked: true,
                    locked
                }
            ];

            if (this.canModerate) {
                manage.push({
                    key: 'auto-moderation',
                    text: 'Auto Moderation',
                    description:
                        'Rules run only on your authenticated social accounts: Facebook pages and groups, ' +
                        'Instagram accounts, X/Twitter accounts and owned custom integrations. They identify ' +
                        'specific patterns of posts and act in real time:',
                    actions: MODERATION_ACTIONS,
                    footnote:
                        'Mostly used for spam moderation at scale. Getting spam on your Facebook page? ' +
                        'We can activate this for you. Contact Affogata to learn more, or try it yourself.',
                    icon: AutoModerationIcon,
                    href: this.buildUrl('/rules'),
                    locked
                });
            }

            const admin = [
                // {
                //     key: 'data-governance',
                //     text: 'Data Governance',
                //     description: 'Govern every data door: API, DIL, MCP, clusters, users and permissions.',
                //     icon: DataGovernanceIcon,
                //     link: '/account/settings',
                //     value: '/account',
                //     isNew: true,
                //     locked
                // }
            ];

            const groups = [
                { label: 'ANALYZE', items: analyze },
                { label: 'SUPPORT', items: support },
                { label: 'MANAGE', items: manage, atBottom: true },
                { label: 'ADMIN', items: admin, atBottom: true }
            ].filter(group => group.items.length);

            const firstAtBottom = groups.find(group => group.atBottom);

            return groups.map(group => ({ ...group, first: group === firstAtBottom }));
        }
    },

    watch: {
        '$route.fullPath'() {
            this.loadingKey = null;
        }
    },

    mounted() {
        window.addEventListener('pageshow', this.handlePageShow);
    },

    beforeUnmount() {
        window.removeEventListener('pageshow', this.handlePageShow);
    },

    methods: {
        ...mapActions({
            setMenuShowing: 'setMenuShowing',
            storeAccountEvent: 'accountEvent/store'
        }),

        buildUrl(path = '') {
            return `${url.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
        },

        isSelected(item) {
            return !item.locked && !!item.value && item.value === this.currentKey;
        },

        isLoading(item) {
            return this.loadingKey === item.key;
        },

        handlePageShow() {
            this.loadingKey = null;
        },

        waitForPaint() {
            return new Promise(resolve => {
                const finish = () => {
                    clearTimeout(timeout);

                    resolve();
                };

                const timeout = setTimeout(finish, PAINT_TIMEOUT);

                this.$nextTick(() => {
                    requestAnimationFrame(() => requestAnimationFrame(finish));
                });
            });
        },

        wrapper(item) {
            if (item.locked) {
                return { is: 'span', attrs: {} };
            }

            if (item.link) {
                return { is: 'router-link', attrs: { to: item.link } };
            }

            return { is: 'a', attrs: { href: item.href } };
        },

        async handleMenuClick(item, e) {
            if (item.locked) {
                return;
            }

            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                return;
            }

            if (this.loadingKey) {
                e.preventDefault();

                return;
            }

            this.loadingKey = item.key;

            this.setMenuShowing(false);

            if (item.link) {
                return;
            }

            e.preventDefault();

            await this.waitForPaint();

            if (item.tracked) {
                try {
                    await this.storeAccountEvent({
                        action_type: ACTION_TYPE_PAGE_VIEW,
                        event_type: EVENT_TYPE_CLICK,
                        action_module: ACTION_MODULE_NEW_UI_NAVIGATION_CHANGE
                    });
                } catch (error) {
                    console.error(error);
                }
            }

            window.location.href = item.href;
        }
    }
};
</script>

<style lang="scss" scoped>
$slate: #2E3A4B;
$selected: #1677ff;
$on-slate: rgba(255, 255, 255, 0.82);
$on-slate-faint: rgba(255, 255, 255, 0.55);

.rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    padding: 8px 0;
    background: $slate;
    overflow: hidden;
}

.rail--busy .navitem:not(.navitem--locked) {
    cursor: wait;
}

.rail__group {
    margin: 5px 0 2px;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 7px;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.38);
    text-align: center;
}

.rail__grow {
    flex: 1 1 auto;
    min-height: 0;
}

.navitem {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    width: 59px;
    min-height: 48px;
    margin: 1px 0;
    padding: 5px 2px;
    border-radius: 6px;
    color: $on-slate;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;

        .navitem__name {
            color: #fff;
        }
    }

    &--on {
        background: $selected;
        box-shadow: 0 4px 12px rgba(22, 119, 255, 0.45);
        color: #fff;

        .navitem__name {
            color: #fff;
            font-weight: 700;
        }
    }

    &--locked {
        cursor: not-allowed;
        opacity: 0.55;
    }

    &--loading {
        cursor: wait;

        .navitem__name {
            opacity: 0.75;
        }
    }
}

.navitem__icon {
    display: block;
    width: 21px;
    height: 21px;
    font-size: 19px;
    line-height: 21px;
}

.navitem__name {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 10px;
    line-height: 1.12;
    letter-spacing: -0.01em;
    text-align: center;
    color: $on-slate-faint;
}

.navitem__new {
    position: absolute;
    top: 3px;
    right: 9px;
    z-index: 2;
    width: 7px;
    height: 7px;
    border: 1.5px solid $slate;
    border-radius: 50%;
    background: #52c41a;
}

.navitem__lock {
    position: absolute;
    top: 3px;
    right: 5px;
    font-size: 10px;
    line-height: 1;
}

@media (max-height: 700px) {
    .rail {
        padding: 4px 0;
    }

    .rail__group {
        margin: 3px 0 1px;
    }

    .navitem {
        gap: 2px;
        min-height: 40px;
        margin: 0;
        padding: 3px 2px;
    }

    .navitem__icon {
        width: 18px;
        height: 18px;
        font-size: 17px;
        line-height: 18px;
    }

    .navitem__name {
        font-size: 9px;
    }
}
</style>

<style lang="scss">
.nav-tip {
    max-width: none;

    .ant-tooltip-inner {
        width: 220px;
        min-height: 0;
        padding: 8px 11px;
        border-radius: 7px;
        font-size: 11.5px;
        line-height: 1.4;
        text-align: left;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28);
    }

    .nav-tip__badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        margin-bottom: 6px;
        padding: 2px 7px;
        border-radius: 20px;
        background: rgba(82, 196, 26, 0.16);
        color: #95de64;
        font-size: 9px;
        font-weight: 600;
        letter-spacing: 0.06em;

        .anticon svg {
            width: 9px;
            height: 9px;
        }
    }

    .nav-tip__title {
        display: block;
        margin-bottom: 1px;
        font-size: 12px;
        font-weight: 700;
    }

    .nav-tip__desc {
        display: block;
        color: rgba(255, 255, 255, 0.85);
    }

    // Auto Moderation lists the actions a rule can take as chips.
    .nav-tip__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin: 7px 0;

        span {
            padding: 1px 7px;
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.14);
            font-size: 10.5px;
        }
    }
}
</style>